import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { productService } from '@/services/productService';
import { ProductList } from '@/components/products/ProductList';
import { ProductForm } from '@/components/products/ProductForm';
import { ImportModal } from '@/components/products/ImportModal';
import { PremiumBadge } from '@/components/billing';
import { Breadcrumbs } from '@/components/shared';
import type { Product, CreateProductInput } from '@/types/product.types';
import { toast } from '@/hooks/useToast';
import { Button } from '@/components/ui/Button';
import { Package, Upload, Lock } from 'lucide-react';
import { createLogger } from '@/lib/logger';

const log = createLogger('ProductsPage');

type ViewMode = 'list' | 'create' | 'edit';

export function ProductsPage() {
  const { profile, currentRestaurantId } = useAuth();
  const { hasFeature } = useFeatureAccess();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);

  const canImport = hasFeature('import_csv');

  // Charger les produits au montage
  useEffect(() => {
    if (currentRestaurantId) {
      loadProducts();
    }
  }, [currentRestaurantId]);

  const loadProducts = async () => {
    if (!currentRestaurantId) return;

    setIsFetching(true);
    const result = await productService.getProducts(currentRestaurantId);

    if (result.success && result.data) {
      setProducts(result.data);
    } else {
      toast({
        title: 'Erreur',
        description: result.error || 'Impossible de charger les produits',
        variant: 'destructive',
      });
    }
    setIsFetching(false);
  };

  const handleCreate = async (input: CreateProductInput) => {
    if (!currentRestaurantId) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await productService.createProduct(currentRestaurantId, input);

      if (result.success) {
        toast({
          title: 'Succès',
          description: 'Le produit a été créé avec succès',
        });
        await loadProducts();
        setViewMode('list');
      } else {
        toast({
          title: 'Erreur',
          description: result.error || 'Impossible de créer le produit',
          variant: 'destructive',
        });
      }
    } catch (error) {
      log.error('Exception in handleCreate', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur inattendue est survenue',
        variant: 'destructive',
      });
    }

    setIsLoading(false);
  };

  const handleUpdate = async (input: CreateProductInput) => {
    if (!selectedProduct) return;

    setIsLoading(true);
    const result = await productService.updateProduct({
      id: selectedProduct.id,
      ...input,
    });

    if (result.success) {
      toast({
        title: 'Succès',
        description: 'Le produit a été mis à jour avec succès',
      });
      await loadProducts();
      setViewMode('list');
      setSelectedProduct(undefined);
    } else {
      toast({
        title: 'Erreur',
        description: result.error || 'Impossible de mettre à jour le produit',
        variant: 'destructive',
      });
    }
    setIsLoading(false);
  };

  const handleDelete = async (productId: string) => {
    setIsLoading(true);
    const result = await productService.deleteProduct(productId);

    if (result.success) {
      toast({
        title: 'Succès',
        description: 'Le produit a été supprimé avec succès',
      });
      await loadProducts();
    } else {
      toast({
        title: 'Erreur',
        description: result.error || 'Impossible de supprimer le produit',
        variant: 'destructive',
      });
    }
    setIsLoading(false);
  };

  const handleEdit = (product: Product) => {
    setSelectedProduct(product);
    setViewMode('edit');
  };

  const handleCancel = () => {
    setViewMode('list');
    setSelectedProduct(undefined);
  };

  if (isFetching) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="mb-4 h-10 w-10 sm:h-12 sm:w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto"></div>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Chargement des produits...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-8 pb-20 sm:pb-8">
      <div className="mx-auto max-w-7xl">
        {/* Breadcrumbs */}
        <Breadcrumbs className="mb-4" />

        {/* En-tête */}
        <div className="mb-4 sm:mb-8 space-y-3 sm:space-y-0 sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">Gestion des produits</h1>
            <p className="mt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              {products.length} produit{products.length > 1 ? 's' : ''} au catalogue
            </p>
          </div>
          <div className="flex gap-2 sm:gap-3">
            {viewMode === 'list' && (
              <>
                <Button
                  variant="outline"
                  onClick={() => canImport ? setShowImportModal(true) : navigate('/pricing')}
                  size="sm"
                  className="text-xs sm:text-sm"
                >
                  {canImport ? (
                    <Upload size={14} className="sm:size-4" />
                  ) : (
                    <Lock size={14} className="sm:size-4" />
                  )}
                  <span className="hidden sm:inline ml-1">Importer</span>
                  {!canImport && <PremiumBadge className="ml-1 hidden sm:inline-flex" />}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/inventory')}
                  size="sm"
                  className="text-xs sm:text-sm"
                >
                  <Package size={14} className="sm:size-4" />
                  <span className="hidden sm:inline ml-1">Gérer les stocks</span>
                  <span className="sm:hidden ml-1">Stocks</span>
                </Button>
              </>
            )}
            {viewMode !== 'list' && (
              <Button variant="outline" onClick={handleCancel} size="sm" className="text-xs sm:text-sm">
                <span className="hidden sm:inline">Retour à la liste</span>
                <span className="sm:hidden">Retour</span>
              </Button>
            )}
          </div>
        </div>

        {/* Contenu principal */}
        <div className="rounded-lg bg-white dark:bg-gray-800 p-4 sm:p-6 shadow">
          {viewMode === 'list' && (
            <ProductList
              products={products}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onAdd={() => setViewMode('create')}
            />
          )}

          {viewMode === 'create' && (
            <div>
              <h2 className="text-base sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">Créer un nouveau produit</h2>
              <ProductForm onSubmit={handleCreate} onCancel={handleCancel} isLoading={isLoading} />
            </div>
          )}

          {viewMode === 'edit' && selectedProduct && (
            <div>
              <h2 className="text-base sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">Modifier le produit</h2>
              <ProductForm
                product={selectedProduct}
                onSubmit={handleUpdate}
                onCancel={handleCancel}
                isLoading={isLoading}
              />
            </div>
          )}
        </div>
      </div>

      {/* Import Modal */}
      {currentRestaurantId && (
        <ImportModal
          open={showImportModal}
          onClose={() => setShowImportModal(false)}
          restaurantId={currentRestaurantId}
          onImportComplete={loadProducts}
        />
      )}
    </div>
  );
}
